import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { ParticipantProvider } from './context/ParticipantContext';
import { BrandingProvider } from './context/BrandingContext';
import HumanistixLanding from './pages/HumanistixLanding';
import QuickscanIntroPage from './pages/QuickscanIntroPage';
import Assessment from './pages/Assessment';
import AssessmentResults from './pages/AssessmentResults';
import UseCasesPage from './pages/UseCasesPage';
import UseCaseDetailPage from './pages/UseCaseDetailPage';
import WorkshopPage from './pages/WorkshopPage';
import WorkshopBookingPage from './pages/WorkshopBookingPage';
import ROICalculatorPage from './pages/ROICalculatorPage';
import ROIComparePage from './pages/ROIComparePage';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import UserDashboard from './pages/UserDashboard';
import AboutPage from './pages/AboutPage';
import TeamPage from './pages/TeamPage';
import ContactPage from './pages/ContactPage';
import { CaseStudiesPage, DocsPage, PrivacyPage, TermsPage, CookiesPage } from './pages/ComingSoonPage';
import CareersPage from './pages/CareersPage';
import BlogPage from './pages/BlogPage';
import FAQPage from './pages/FAQPage';
import BetaFeedbackButton from './components/BetaFeedbackButton';
import ScrollToTop from './components/ScrollToTop';
import { usePageviewTracker } from './hooks/usePageviewTracker';
import { Loader2 } from 'lucide-react';

// Protected Route component for authenticated users
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, initialized } = useAuth();
  const location = useLocation();

  // Show loading while checking auth
  if (!initialized || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Router component that handles session_id detection
const AppRouter = () => {
  const location = useLocation();

  // Anonymous, cookieless website analytics tracker (skips /admin/*)
  usePageviewTracker();

  // Check URL fragment for session_id SYNCHRONOUSLY during render
  // This prevents race conditions with ProtectedRoute
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HumanistixLanding />} />
      <Route path="/quickscan/start" element={<Assessment />} />
      <Route path="/quickscan" element={<QuickscanIntroPage />} />
      <Route path="/quickscan/results" element={<AssessmentResults />} />
      <Route path="/assessment" element={<Navigate to="/quickscan/start" replace />} />
      <Route path="/assessment/results" element={<Navigate to="/quickscan/results" replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Static Pages */}
      <Route path="/about" element={<AboutPage />} />
      <Route path="/team" element={<TeamPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/case-studies" element={<CaseStudiesPage />} />
      <Route path="/docs" element={<DocsPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/cookies" element={<CookiesPage />} />
      
      {/* Admin Routes (separate auth) */}
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      
      {/* Public Use Cases Routes - No login required */}
      <Route path="/use-cases" element={<UseCasesPage />} />
      <Route path="/use-cases/:id" element={<UseCaseDetailPage />} />
      <Route path="/use-cases/:id/workshop" element={<WorkshopPage />} />
      <Route path="/workshop" element={<WorkshopBookingPage />} />
      <Route path="/book-workshop" element={<WorkshopBookingPage />} />
      <Route path="/use-cases/:id/roi" element={<ROICalculatorPage />} />
      <Route path="/roi-compare" element={<ROIComparePage />} />
      
      {/* Protected Routes - Require User Authentication */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <UserDashboard />
        </ProtectedRoute>
      } />
    </Routes>
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <BrandingProvider>
              <ParticipantProvider>
                <AppRouter />
                {/* Beta Testing Feedback Button - visible on all pages */}
                <BetaFeedbackButton />
              </ParticipantProvider>
            </BrandingProvider>
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
